import { ExternalLink, Eye, Heart, MessageCircle, Repeat2, TrendingUp, TrendingDown, Trophy, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { PF, n, delta, pct, dd } from "@/lib/reports";
import { photoUrl } from "@/components/clients/ClientPhoto";
import { lastName } from "@/lib/constants";

const Stat = ({ icon: Icon, v, label }) => (
  <div className="rounded-2xl bg-zinc-800/40 px-3 py-2.5"><div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-zinc-500"><Icon className="h-3.5 w-3.5" />{label}</div>
    <div className="mt-0.5 font-mono text-xl font-bold text-zinc-100">{n(v)}</div></div>
);

const PostRow = ({ p, rank }) => (
  <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`report-post-${p.platform}-${p.post_id}`}
    className="flex items-center gap-3 rounded-2xl px-2 py-2 hover:bg-zinc-800/40 transition-colors">
    {rank && <span className="w-5 text-center font-display font-bold text-amber-500">{rank}</span>}
    {p.thumb ? <img src={p.thumb} alt="" referrerPolicy="no-referrer" loading="lazy" className="h-12 w-12 shrink-0 rounded-xl object-cover bg-zinc-800" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
      : <span className="h-12 w-12 shrink-0 rounded-xl" style={{ background: PF[p.platform].color }} />}
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm text-zinc-200">{p.caption || "Fără descriere"}</div>
      <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-zinc-500">
        <span style={{ color: PF[p.platform].color }} className="font-semibold">{PF[p.platform].label}</span>
        {p.posted_at && <span>{dd(p.posted_at.slice(0, 10))}</span>}
        <span>👁 {n(p.views)}</span><span>❤️ {n(p.likes)}</span><span>💬 {n(p.comments)}</span>{p.shares != null && <span>↗ {n(p.shares)}</span>}
      </div>
    </div>
    <ExternalLink className="h-4 w-4 shrink-0 text-zinc-600" />
  </a>
);

export const ClientReport = ({ r, photo }) => {
  const ch = pct(r.totals.views, r.prev.views);
  const src = photo ?? photoUrl(r.client);
  return (
    <section className="surface-card relative overflow-hidden rounded-3xl border border-zinc-800/70 bg-[#12141C] p-5 sm:p-7" data-testid={`report-client-${r.client.id}`}>
      <div className="flex items-center gap-4">
        {src ? <img src={src} alt="" className="h-16 w-16 rounded-2xl object-cover" /> : <span className="h-16 w-16 rounded-2xl" style={{ background: r.client.color }} />}
        <div className="min-w-0">
          <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight truncate" title={r.client.name}>{lastName(r.client.name)}</h3>
          <div className="mt-1 flex flex-wrap gap-2">
            {Object.entries(r.growth).map(([p, g]) => (
              <a key={p} href={g.url} target="_blank" rel="noopener noreferrer" data-testid={`report-growth-${r.client.id}-${p}`}
                className="rounded-full border border-zinc-700 px-2.5 py-0.5 text-xs text-zinc-300 hover:border-zinc-500">
                <b style={{ color: PF[p].color }}>{PF[p].short}</b> {n(g.followers)} {g.new != null && <span className={g.new >= 0 ? "text-emerald-500" : "text-rose-500"}>{delta(g.new)}</span>}
              </a>
            ))}
          </div>
        </div>
        {ch != null && <span className={`ml-auto flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${ch >= 0 ? "bg-emerald-500/15 text-emerald-500" : "bg-rose-500/15 text-rose-500"}`} title="Vizualizări față de perioada anterioară">
          {ch >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{ch}%</span>}
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat icon={Eye} v={r.totals.views} label="Vizualizări" /><Stat icon={Heart} v={r.totals.likes} label="Like-uri" />
        <Stat icon={MessageCircle} v={r.totals.comments} label="Comentarii" /><Stat icon={Repeat2} v={r.totals.shares} label="Distribuiri" />
      </div>
      {r.top.length > 0 && <><h4 className="mt-6 mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Top clipuri</h4>{r.top.map((p, i) => <PostRow key={p.key || p.url} p={p} rank={i + 1} />)}</>}
      {r.posts.length > r.top.length && (
        <details className="mt-3"><summary className="cursor-pointer text-sm text-zinc-400" data-testid={`report-all-${r.client.id}`}>Toate postările ({r.totals.posts})</summary>
          {r.posts.map((p) => <PostRow key={p.key || p.url} p={p} />)}</details>
      )}
      {!r.posts.length && <p className="mt-4 text-sm text-zinc-500">Nicio postare în perioada asta.</p>}
    </section>
  );
};

const Move = ({ r }) => {
  if (!r.prev_rank) return <span className="text-xs font-semibold text-sky-400">nou</span>;
  const d = r.prev_rank - r.rank;
  if (!d) return <span className="flex items-center text-zinc-500"><Minus className="h-4 w-4" /></span>;
  return <span className={`flex items-center gap-0.5 text-sm font-semibold ${d > 0 ? "text-emerald-500" : "text-rose-500"}`}>{d > 0 ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}{Math.abs(d)}</span>;
};

export const Leaderboard = ({ rep }) => {
  const max = Math.max(1, ...rep.clients.map((r) => r.totals.views));
  return (
    <section className="surface-card rounded-3xl border border-zinc-800/70 bg-[#12141C] p-5 sm:p-7" data-testid="report-leaderboard">
      <h3 className="flex items-center gap-2 font-display text-xl font-bold"><Trophy className="h-5 w-5 text-amber-500" />Clasament după vizualizări</h3>
      <p className="mt-1 text-xs text-zinc-500">Săgeata arată locul față de perioada anterioară de aceeași lungime.</p>
      <ol className="mt-4 space-y-2">
        {rep.clients.map((r) => (
          <li key={r.client.id} className="grid grid-cols-[2rem_1fr_auto_2.5rem] items-center gap-3" data-testid={`leader-row-${r.client.id}`}>
            <span className={`font-display text-xl font-bold ${r.rank <= 3 ? "text-amber-500" : "text-zinc-500"}`}>{r.rank}</span>
            <div className="min-w-0">
              <div className="truncate font-semibold" title={r.client.name}>{lastName(r.client.name)}</div>
              <div className="mt-1 h-1.5 rounded-full bg-zinc-800"><div className="h-1.5 rounded-full" style={{ width: `${(r.totals.views / max) * 100}%`, background: r.client.color || "#F59E0B" }} /></div>
            </div>
            <span className="font-mono text-sm text-zinc-200">{n(r.totals.views)}</span>
            <span className="flex justify-end" data-testid={`leader-move-${r.client.id}`}><Move r={r} /></span>
          </li>
        ))}
      </ol>
    </section>
  );
};

export const ReportBody = ({ rep, photoOf }) => (
  <div className="space-y-5" data-testid="report-body">
    {rep.clients.length > 1 && <Leaderboard rep={rep} />}
    {rep.clients.map((r) => <ClientReport key={r.client.id} r={r} photo={photoOf?.(r)} />)}
    {!rep.clients.length && <p className="text-zinc-500" data-testid="report-empty">Niciun client cu conturi legate. Adaugă conturile în tabul „Conturi”.</p>}
    <p className="text-xs text-zinc-500">Urmăritorii noi se calculează din istoricul strâns zilnic{rep.last_sync ? ` · ultima citire ${new Date(rep.last_sync).toLocaleString("ro-RO")}` : ""}.</p>
  </div>
);
